// src/services/mercadoPagoService.js
// ═══════════════════════════════════════════════════════════════
// MERCADO PAGO - Integracion de pagos para suscripciones SaaS
// ═══════════════════════════════════════════════════════════════
//
// INSTRUCCIONES PARA CONFIGURAR:
// 1. Crear cuenta en https://www.mercadopago.com.pe/developers
// 2. Obtener credenciales de produccion (Access Token, Public Key)
// 3. Reemplazar los valores en MERCADOPAGO_CONFIG
// 4. Para pruebas usar credenciales de sandbox (TEST-)
//
// FLUJO:
// 1. El admin de empresa hace click en "Upgrade" desde /mi-plan
// 2. Se crea una preferencia de pago en MercadoPago via Cloud Function
// 3. El usuario es redirigido al checkout de MercadoPago
// 4. Al pagar, MercadoPago notifica via webhook (Cloud Function)
// 5. La Cloud Function actualiza el plan del workspace en Firestore
//
import { doc, setDoc, getDoc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

// ═══════════════════════════════════════
// CREDENCIALES - REEMPLAZAR CON LAS TUYAS
// ═══════════════════════════════════════
const MERCADOPAGO_CONFIG = {
  // Public Key - se usa en frontend para inicializar el SDK
  publicKey: 'YOUR_MERCADOPAGO_PUBLIC_KEY',

  // Access Token - SOLO se usa en backend (Cloud Functions)
  // NUNCA exponer en frontend
  // accessToken: 'YOUR_MERCADOPAGO_ACCESS_TOKEN',

  // URLs de retorno despues del pago
  backUrls: {
    success: 'https://hingeniasuite.com/pago-exitoso',
    failure: 'https://hingeniasuite.com/pago-fallido',
    pending: 'https://hingeniasuite.com/pago-pendiente',
  },

  // Webhook URL - configurar en el panel de MercadoPago
  // webhookUrl: 'https://us-central1-gestor-de-respuestas-hingenia.cloudfunctions.net/mercadoPagoWebhook',

  // Modo sandbox para pruebas
  sandbox: true,
};

// Precios en la moneda local (PEN para Peru)
const PLAN_PRICES = {
  pro: {
    monthly: { amount: 49, currency: 'PEN', title: 'Plan Pro - Mensual', description: 'Hingenia Suite Plan Pro - 25 usuarios, todos los modulos' },
    yearly: { amount: 470, currency: 'PEN', title: 'Plan Pro - Anual', description: 'Hingenia Suite Plan Pro - Anual (ahorra 20%)' },
  },
  enterprise: {
    monthly: { amount: 149, currency: 'PEN', title: 'Plan Enterprise - Mensual', description: 'Hingenia Suite Enterprise - Usuarios ilimitados' },
    yearly: { amount: 1430, currency: 'PEN', title: 'Plan Enterprise - Anual', description: 'Hingenia Suite Enterprise - Anual (ahorra 20%)' },
  },
};

/**
 * Obtener la public key de MercadoPago
 */
export const getMercadoPagoPublicKey = () => {
  return MERCADOPAGO_CONFIG.publicKey;
};

/**
 * Verificar si MercadoPago esta configurado
 */
export const isMercadoPagoConfigured = () => {
  return MERCADOPAGO_CONFIG.publicKey !== 'YOUR_MERCADOPAGO_PUBLIC_KEY';
};

/**
 * Obtener precio de un plan
 */
export const getPlanPrice = (planId, billing = 'monthly') => {
  return PLAN_PRICES[planId]?.[billing] || null;
};

/**
 * Crear una orden de pago (se guarda en Firestore)
 * La Cloud Function la procesara y creara la preferencia en MercadoPago
 */
export const createPaymentOrder = async (workspaceId, planId, billing = 'monthly', userData = {}) => {
  const price = getPlanPrice(planId, billing);
  if (!price) throw new Error('Plan no encontrado');

  const order = {
    workspaceId,
    planId,
    billing,
    amount: price.amount,
    currency: price.currency,
    title: price.title,
    description: price.description,
    status: 'pending', // pending | processing | approved | rejected | cancelled
    paymentProvider: 'mercadopago',
    userEmail: userData.email || '',
    userName: userData.name || '',
    mercadoPagoPreferenceId: null,
    mercadoPagoPaymentId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'payment_orders'), order);
  return { id: ref.id, ...order };
};

/**
 * Obtener ordenes de pago de un workspace
 */
export const getPaymentOrders = async (workspaceId) => {
  const { query, where, orderBy, getDocs } = await import('firebase/firestore');
  const q = query(
    collection(db, 'payment_orders'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Registrar pago exitoso (llamado por webhook/Cloud Function)
 */
export const recordSuccessfulPayment = async (orderId, mercadoPagoData) => {
  const orderRef = doc(db, 'payment_orders', orderId);

  await updateDoc(orderRef, {
    status: 'approved',
    mercadoPagoPaymentId: mercadoPagoData.paymentId,
    mercadoPagoStatus: mercadoPagoData.status,
    paidAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Obtener la orden para saber que plan activar
  const orderSnap = await getDoc(orderRef);
  const order = orderSnap.data();

  // Activar el plan en el workspace
  const { changeWorkspacePlan } = await import('./planService');
  await changeWorkspacePlan(order.workspaceId, order.planId);

  // Registrar en historial de pagos
  await addDoc(collection(db, 'payment_history'), {
    workspaceId: order.workspaceId,
    orderId,
    planId: order.planId,
    amount: order.amount,
    currency: order.currency,
    billing: order.billing,
    paymentProvider: 'mercadopago',
    mercadoPagoPaymentId: mercadoPagoData.paymentId,
    paidAt: serverTimestamp(),
  });

  return order;
};

/**
 * Guardar configuracion de MercadoPago en Firestore (super-admin)
 */
export const saveMercadoPagoConfig = async (config) => {
  await setDoc(doc(db, 'app_config', 'mercadopago'), {
    publicKey: config.publicKey || '',
    webhookConfigured: config.webhookConfigured || false,
    sandbox: config.sandbox !== undefined ? config.sandbox : true,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Obtener configuracion de MercadoPago desde Firestore
 */
export const getMercadoPagoConfig = async () => {
  const snap = await getDoc(doc(db, 'app_config', 'mercadopago'));
  if (!snap.exists()) return { publicKey: '', sandbox: true, webhookConfigured: false };
  return snap.data();
};

// ═══════════════════════════════════════
// CLOUD FUNCTION TEMPLATE (para referencia)
// ═══════════════════════════════════════
//
// Crear en functions/index.js:
//
// const mercadopago = require('mercadopago');
// const functions = require('firebase-functions');
// const admin = require('firebase-admin');
//
// mercadopago.configure({
//   access_token: functions.config().mercadopago.access_token,
// });
//
// // Crear preferencia de pago
// exports.createMercadoPagoPreference = functions.https.onCall(async (data, context) => {
//   const { orderId } = data;
//   const orderSnap = await admin.firestore().doc(`payment_orders/${orderId}`).get();
//   const order = orderSnap.data();
//
//   const preference = {
//     items: [{
//       title: order.title,
//       unit_price: order.amount,
//       quantity: 1,
//       currency_id: order.currency,
//     }],
//     back_urls: {
//       success: 'https://hingeniasuite.com/pago-exitoso?order=' + orderId,
//       failure: 'https://hingeniasuite.com/pago-fallido?order=' + orderId,
//       pending: 'https://hingeniasuite.com/pago-pendiente?order=' + orderId,
//     },
//     external_reference: orderId,
//     auto_return: 'approved',
//   };
//
//   const response = await mercadopago.preferences.create(preference);
//   await admin.firestore().doc(`payment_orders/${orderId}`).update({
//     mercadoPagoPreferenceId: response.body.id,
//     status: 'processing',
//   });
//
//   return { preferenceId: response.body.id, initPoint: response.body.init_point };
// });
//
// // Webhook de notificacion
// exports.mercadoPagoWebhook = functions.https.onRequest(async (req, res) => {
//   const { type, data } = req.body;
//   if (type === 'payment') {
//     const payment = await mercadopago.payment.findById(data.id);
//     if (payment.body.status === 'approved') {
//       const orderId = payment.body.external_reference;
//       // Llamar a recordSuccessfulPayment
//     }
//   }
//   res.sendStatus(200);
// });
